import { useCallback, useState } from 'react';
import { Box, Button, Flex, Form, RadioInput, Text, toast } from 'dash-ui';
import { BrokerLicenseStatusesEnum } from 'dash-common';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { BrokerFragment } from 'gql/generated.graphql';
import { DeActivateBrokerLicenseModal } from './components/DeActivateBrokerLicenseModal/DeActivateBrokerLicenseModal';
import { ActivateBrokerLicenseModal } from './components/ActivateBrokerLicenseModal/ActivateBrokerLicenseModal';
import {
  LicenseData,
  LicenseDataBox,
} from './components/LicenseData/LicenseData';

const LicenseInfo = ({ broker }: { broker?: BrokerFragment }) => {
  const theme = useTheme();
  const { t } = useTranslation(['forms', 'pages', 'common']);
  const [isDeActivateOpen, setDeActivateIsOpen] = useState(false);
  const [isActivateOpen, setActivateIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [licenseData, setLicenseData] = useState<LicenseData>();
  const status = broker?.license?.licenseStatus as BrokerLicenseStatusesEnum;

  const onButtonClick = useCallback(() => {
    if (status === BrokerLicenseStatusesEnum.ACTIVE) {
      setDeActivateIsOpen(true);
    }
    if (
      [
        BrokerLicenseStatusesEnum.INACTIVE,
        BrokerLicenseStatusesEnum.PENDING_REVIEW,
      ].includes(status)
    ) {
      setActivateIsOpen(true);
    }
  }, [status]);

  const onVerifyClick = useCallback(async () => {
    setLoading(true);
    await fetch(
      `${process.env['REACT_APP_REST']}/license/${broker?.license?.licenseNumber}`,
      {
        headers: {
          api_key: process.env['REACT_APP_API_KEY'] || '',
          'Content-Type': 'application/json',
        },
      }
    )
      .then((res) => res.json())
      .then((jsonResponse) => {
        if (jsonResponse.statusCode)
          toast.error(t('common:serverMessages.licenseValidationError'));
        else setLicenseData(jsonResponse);
      });
    setLoading(false);
  }, [broker]);

  return (
    <Box p={6}>
      <Flex flexDirection="row" justifyContent="space-between">
        <Flex flexDirection="column">
          <Text semibold>{t('pages:brokersPage.licenseInfo')}</Text>
          <Flex mt={2} alignItems="center">
            <Form.Group maxWidth={394} w="100%">
              <Form.Label>{t('forms:labels.brokerLicenseNumber')}</Form.Label>
              <Form.Input
                value={broker?.license?.licenseNumber || ''}
                placeholder={t('forms:placeholders.brokerLicenseNumber') || ''}
                iconRight={
                  status === BrokerLicenseStatusesEnum.ACTIVE
                    ? 'check'
                    : 'close'
                }
                iconRightProps={{
                  color:
                    status === BrokerLicenseStatusesEnum.ACTIVE
                      ? theme.colors.g20
                      : theme.colors.r20,
                  size: 20,
                }}
              />
            </Form.Group>
            <Button
              mt={5}
              ml={2}
              h={46}
              onClick={onVerifyClick}
              disabled={!broker?.license?.licenseNumber}
              isLoading={loading}
            >
              {t('pages:brokersPage.verifyBrokerLicense')}
            </Button>
          </Flex>
          <Flex mt={3}>
            <Text semibold>{t('forms:labels.agentLicenseStatus')}:</Text>
            <Flex alignItems="center">
              <RadioInput
                mr={2}
                ml={5.5}
                onClick={onButtonClick}
                checked={false}
              />
              <Text>
                {status === BrokerLicenseStatusesEnum.ACTIVE
                  ? t('pages:brokersPage.markAsInvalid')
                  : t('pages:brokersPage.markValid')}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex>
          {licenseData && <LicenseDataBox licenseData={licenseData} />}
        </Flex>
        <Flex></Flex>
      </Flex>
      {broker && (
        <DeActivateBrokerLicenseModal
          isOpen={isDeActivateOpen}
          onClose={() => setDeActivateIsOpen(false)}
          brokerId={broker._id}
        />
      )}
      {broker && (
        <ActivateBrokerLicenseModal
          isOpen={isActivateOpen}
          onClose={() => setActivateIsOpen(false)}
          brokerId={broker._id}
        />
      )}
    </Box>
  );
};

export default LicenseInfo;
